import moment from 'moment';
import { useSelector, useDispatch } from "react-redux";

import { API, graphqlOperation } from 'aws-amplify';
import {mapper, APP_ACCESS_LEVEL, ACCESS_LEVEL_IDs} from './constants/AppConstants'
import { executeQuery, getBrassAllLDAPS } from 'src/utilities/AppSyncPlusAPIs';

// Browser-level caching
const LDAP_CACHE_TTL = 60 * 60 * 1000; // 60 minutes
const CACHE_PREFIX = 'brass_cache';

// returns a valid(unexpired) cache value
export const getCachedResult = (cacheKey) => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { result, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < LDAP_CACHE_TTL) {
        return result;
      }
      localStorage.removeItem(cacheKey);
    }
  } catch (e) {
    localStorage.removeItem(cacheKey);
  }
  return null;
}

export const getCachedLdaps = (stage) => {
  return fetchAndCacheAllLdaps(stage)
}

const setCachedResult = (cacheKey, result) => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ result, timestamp: Date.now() }));
  } catch (e) {
    // Silent fail
  }
}

function checkAuth(authorizedGroups, userLDAPS){
  let result = false
  const authorized =  authorizedGroups.find((group) => (
    userLDAPS.includes(group)
 ))
  if(authorized) {
    result = true;
  }
  return result
}

export const fetchAndCacheAllLdaps = async (stage) => {
  const cacheKey = `${CACHE_PREFIX}:all_ldaps:${stage}`;
  const cached = getCachedResult(cacheKey);
  if (cached) {
    return cached;
  }

  return new Promise((resolve, reject) => {
    stage = (stage === 'dev' ? 'beta' : stage); // use stage level mapping only for API calls and not while saving cache.
    getBrassAllLDAPS(stage, 
      (response) => {
        const ldaps = response.data.getBrassAllLDAPS;
        setCachedResult(cacheKey, ldaps);
        resolve(ldaps);
      },
      (error) => {
        reject(error);
      }
    );
  });
};

export const checkLdapMembershipFromCache = async (stage, ldap) => {
  const cacheKey = `${CACHE_PREFIX}:all_ldaps:${stage}`;
  const allLdapsCache = JSON.parse(getCachedResult(cacheKey)) || null;
  if (!allLdapsCache) {
    return null;
  }
  const isMember = allLdapsCache.includes(ldap);
  return isMember;
};

export const isMemberOf = async (userId, ldap, stage, userLDAPS=null) => {
  try {
    await fetchAndCacheAllLdaps(stage); // this will load the cache if expired or unavailable
    const result = checkLdapMembershipFromCache(stage, ldap); // this will lookup to cache and return if there is a matching LDAP
    if (result !== null) {
      return result;
    }
  } catch (error) {
    // Silent fail
  }
  
  return false;
}


const checkAccess = async (userAlias, id, accessLevelId, stage, key = undefined) => {
  const cacheKey = `${CACHE_PREFIX}:${userAlias}:${id}:${accessLevelId}:${stage}:${key || 'undefined'}`;
  const cachedResult = getCachedResult(cacheKey);
  if (cachedResult !== null) {
    return cachedResult;
  }
  
  let mQry;
  stage = (stage === 'dev' ? 'beta' : stage) // use stage level mapping only for API calls and not while saving cache.
  if(key === undefined) {
    mQry =
      `  query MyQuery {
        brassCheckAccess(id: "${id}", accessLevelId: ${accessLevelId},  stage: "${stage}") {
          is_authorized
          optional_data
        }
      }
    `;
  } else {
    mQry =
    `  query MyQuery {
      brassCheckAccess(id: "${id}", accessLevelId: ${accessLevelId},  stage: "${stage}", key: "${key}") {
        is_authorized
        optional_data
      }
    }
  `;
  }

  let response = await API.graphql(graphqlOperation(mQry));
	let brassResponse = response.data.brassCheckAccess;
  setCachedResult(cacheKey, brassResponse);
	return brassResponse;
}

export const checkGeneralAccess = async ({userAlias, id, stage}) => {
  const response = await checkAccess(userAlias, id, ACCESS_LEVEL_IDs.APP_GENERAL_ACCESS, stage)
  return response["is_authorized"];
}

export const checkAppAdminAccess = async ({userAlias, id, stage}) => {
  const response = await checkAccess(userAlias, id, ACCESS_LEVEL_IDs.APP_ADMIN, stage)
  return response["is_authorized"];
}

// Data in optionalAccessLevelIds is ranked (Whichever comes first has higher presidence over the rest)
export const getAccessLevel = async (userAlias, id, stage, optionalAccessLevelIds = []) => {

  const accessLevelResponse = {
    appAccessLevel: ACCESS_LEVEL_IDs.NO_ACCESS,
    optionalData: null
  }
  
  //App Admin
  const appAdminResponse = await checkAccess(userAlias, id, ACCESS_LEVEL_IDs.APP_ADMIN, stage)
  if(appAdminResponse["is_authorized"]) {
    accessLevelResponse["appAccessLevel"] = ACCESS_LEVEL_IDs.APP_ADMIN
    return accessLevelResponse
  }

  //Full Edit
  const fullEditResponse = await checkAccess(userAlias, id, ACCESS_LEVEL_IDs.FULL_EDIT, stage)
  if(fullEditResponse["is_authorized"]) {
    accessLevelResponse["appAccessLevel"] = ACCESS_LEVEL_IDs.FULL_EDIT
    return accessLevelResponse
  }

  // Partial Edit
  for (let accessLevelId of optionalAccessLevelIds) {
    const response = await checkAccess(userAlias, id, accessLevelId, stage)
    if(response["is_authorized"]) {
      accessLevelResponse["appAccessLevel"] = ACCESS_LEVEL_IDs.PARTIAL_EDIT
      accessLevelResponse["optionalData"] = response["optional_data"]
      return accessLevelResponse
    }
  }

  //Read Only
  const readOnlyResponse = await checkAccess(userAlias, id, ACCESS_LEVEL_IDs.READ_ONLY, stage)
  if(readOnlyResponse["is_authorized"]) {
    accessLevelResponse["appAccessLevel"] = ACCESS_LEVEL_IDs.READ_ONLY
    return accessLevelResponse
  }

  return accessLevelResponse;

}


export const getAccessLevelCorpReporting = async (userAlias, appId, stage) => {

  const accessLevelResponse = {
    appAccessLevel: ACCESS_LEVEL_IDs.NO_ACCESS,
    optionalData: null
  }
  
  //App Admin
  const appAdminResponse = await checkAccess(userAlias, appId, ACCESS_LEVEL_IDs.APP_ADMIN, stage)
  if(appAdminResponse["is_authorized"]) {
    accessLevelResponse["appAccessLevel"] = ACCESS_LEVEL_IDs.APP_ADMIN
    return accessLevelResponse
  }


  return accessLevelResponse;

}

export const checkAppFunctionalityAccess = async ({userAlias, id, accessLevelId, stage, key}) => {
  
  const response = await checkAccess(userAlias, id, accessLevelId, stage, key)
  return response["is_authorized"];

}

export const checkIfPortalAdmin = async (userAlias, stage) => {
  const response = await checkAccess(userAlias, "ABC_ADMIN", ACCESS_LEVEL_IDs.PORTAL_ADMIN, stage)
  return response["is_authorized"];
}

export const checkIfSupportAdmin = async (userAlias, stage) => {
  const response = await checkAccess(userAlias, "SUPPORT_TEAM_ADMIN", ACCESS_LEVEL_IDs.APP_ADMIN, stage)
  return response["is_authorized"];
}

// RAISE admins can edit the RAISE curriculum — members of the RAISE_ADMINS
// group (APP_ADMIN level), or portal admins, checked via Brass.
export const checkIfRaiseAdmin = async (userAlias, stage) => {
  const response = await checkAccess(userAlias, "RAISE_ADMINS", ACCESS_LEVEL_IDs.APP_ADMIN, stage)
  if (response["is_authorized"]) {
    return true;
  }
  return await checkIfPortalAdmin(userAlias, stage);
}

export const isFirstPassProFullUser = async (userAlias, stage) => {
  const response = await checkAccess(userAlias, "FIRST_PASS_PRO", ACCESS_LEVEL_IDs.FULL_EDIT, stage)
  return response["is_authorized"];
}

async function checkPageAccess(AppId, userLDAPS) {

  let result = true;
     
  if(AppId === mapper?.boffin?.id) {
    result = checkAuth(mapper.boffin.groups, userLDAPS)
  }else if(AppId ===  mapper?.mjemonitoring?.id) {
    result = checkAuth(mapper.mjemonitoring.groups, userLDAPS)
}

// if(AppId ===  mapper?.ted?.id) {
  //   result = checkAuth(mapper.ted.groups, userLDAPS)
  //  }
 
  return result;
}



export default checkPageAccess